/**
 * Cadre juridique publié dans l'application.
 *
 * Ce n'est pas un acte notarié : c'est la position produit, écrite pour être
 * lue, et à faire relire par un juriste avant l'ouverture au public. Les
 * montants (forfait no-show, délai d'annulation) sont ceux du code, pas une
 * copie figée qui divergerait.
 */

import { PAYMENT_DISCLOSURE } from '../domain/order';
import { MIN_REVIEWS_FOR_AVERAGE } from '../domain/reviews';

export const LEGAL_VERSION = '2026-08-26';
export const LEGAL_UPDATED_LABEL = '26 août 2026';

export const LEGAL_CONTACT_EMAIL = 'bonjour@champtable.consol.fr';
export const LEGAL_SITE_URL = 'https://champtable.consol.fr';

export type LegalSlug =
  | 'mentions-legales'
  | 'cgu'
  | 'cgv'
  | 'cgu-producteurs'
  | 'confidentialite'
  | 'charte-avis';

export interface LegalArticle {
  readonly title: string;
  readonly paragraphs: readonly string[];
}

export interface LegalDocument {
  readonly slug: LegalSlug;
  readonly title: string;
  readonly shortTitle: string;
  readonly lead: string;
  readonly articles: readonly LegalArticle[];
}

const DRAFT_LEAD =
  'Version de travail, alignée sur le fonctionnement actuel de Champ’Table. Elle sera relue par un juriste avant toute ouverture au public.';

function article(title: string, ...paragraphs: string[]): LegalArticle {
  return { title, paragraphs };
}

const mentions: LegalDocument = {
  slug: 'mentions-legales',
  title: 'Mentions légales',
  shortTitle: 'Mentions légales',
  lead: DRAFT_LEAD,
  articles: [
    article(
      'Éditeur',
      'Le service Champ’Table, accessible à l’adresse ' +
        LEGAL_SITE_URL +
        ', est édité et exploité par Consol, ci-après « l’éditeur ».',
      'Les mentions d’immatriculation (forme sociale, capital, RCS, SIRET, siège) seront complétées avant l’ouverture au public. Tant qu’elles manquent, ce document ne vaut pas publication définitive au sens de la LCEN.',
      'Contact : ' + LEGAL_CONTACT_EMAIL + '.',
    ),
    article(
      'Hébergement',
      'Le site et l’API sont hébergés par Amazon Web Services EMEA SARL, 38 avenue John F. Kennedy, L-1855 Luxembourg. Les traitements applicatifs et les données sont localisés dans la région eu-west-3 (Paris, France).',
    ),
    article(
      'Propriété intellectuelle',
      'Le nom Champ’Table, le slogan, le logo, les textes, la charte graphique et les éléments logiciels de la plateforme sont protégés. Toute reproduction non autorisée est interdite.',
      'La vérification de disponibilité du nom auprès de l’INPI et de l’AFNIC, puis le dépôt de marque, restent une démarche de l’éditeur, distincte de la mise en ligne de ces pages.',
    ),
    article(
      'Contenu des producteurs',
      'Les photographies, descriptions, prix et informations d’exploitation publiés sur une boutique sont fournis par le producteur, qui en reste responsable. Champ’Table peut retirer un contenu manifestement illicite après signalement.',
    ),
  ],
};

const cgu: LegalDocument = {
  slug: 'cgu',
  title: 'Conditions générales d’utilisation',
  shortTitle: 'CGU',
  lead: DRAFT_LEAD,
  articles: [
    article(
      'Objet et qualification',
      'Champ’Table est une place de marché numérique de vente directe, en circuit court. Elle met en relation des producteurs et des clients. Elle n’achète ni ne revend les denrées : elle n’est pas revendeur, mais intermédiaire technique.',
      'Le contrat de vente porte entre le client et le producteur. Champ’Table n’entre pas dans ce contrat, hors les services de plateforme décrits ici et dans les conditions générales de vente.',
    ),
    article(
      'Compte',
      'La consultation du catalogue est libre, sans compte. Un compte est exigé pour commander, laisser un avis, ou activer un profil producteur.',
      'Un même compte sert à acheter et, le cas échéant, à vendre. L’utilisateur s’engage à fournir des informations exactes et à préserver la confidentialité de ses identifiants.',
    ),
    article(
      'Services de la plateforme',
      'Champ’Table fournit l’accès au catalogue, la commande, le paiement en ligne ou l’empreinte de réservation via un prestataire de paiement, les notifications liées à la commande, et les outils d’avis.',
      'La plateforme peut évoluer, être interrompue pour maintenance, ou restreindre un compte en cas d’usage abusif, de fraude ou de non-respect des présentes.',
    ),
    article(
      'Données personnelles',
      'Le traitement des données est décrit dans la politique de confidentialité. En créant un compte, l’utilisateur confirme en avoir pris connaissance.',
    ),
    article(
      'Responsabilité',
      'Champ’Table n’est pas responsable de la qualité, de l’étiquetage, de l’hygiène ou de la conformité des produits, qui incombent au producteur vendeur. Elle n’est pas responsable d’un manquement du producteur au créneau de retrait, hors les mécanismes prévus aux CGV (annulation, no-show).',
      'La responsabilité de Champ’Table au titre de la plateforme est limitée aux dommages directs, à l’exclusion des pertes de chance, dans la limite permise par le droit de la consommation lorsque l’utilisateur est un consommateur.',
    ),
    article(
      'Droit applicable',
      'Les présentes sont régies par le droit français. En cas de litige, une solution amiable est recherchée, notamment via le service client à l’adresse ' +
        LEGAL_CONTACT_EMAIL +
        '. À défaut, les tribunaux français sont compétents, sous réserve des règles impératives de protection du consommateur.',
    ),
  ],
};

const cgv: LegalDocument = {
  slug: 'cgv',
  title: 'Conditions générales de vente',
  shortTitle: 'CGV',
  lead: DRAFT_LEAD,
  articles: [
    article(
      'Vendeur',
      'Chaque produit est vendu par le producteur identifié sur la fiche, et non par Champ’Table. Le prix affiché est celui fixé par le producteur. La facture, lorsqu’elle est due, est émise par le producteur.',
    ),
    article(
      'Commande et retrait',
      'La commande est une réservation à retirer au point et au créneau choisis. Elle n’est pas une livraison à domicile.',
      'Le client peut payer en ligne, ou réserver et payer au retrait lorsque le producteur l’accepte. Un producteur qui n’accepte pas la carte sur place l’indique (« espèces uniquement »).',
    ),
    article(
      'Paiement',
      'Les paiements en ligne et les empreintes bancaires sont réalisés par Stripe, prestataire unique au lancement. Champ’Table ne collecte ni ne conserve de numéro de carte. Apple Pay et Google Pay peuvent être proposés par ce prestataire.',
      'Le paiement en ligne est immédiat. La réservation « payer au retrait » ne demande une empreinte de 5 € qu’après un no-show confirmé sur le téléphone vérifié ; la première réservation n’en nécessite pas.',
    ),
    article(
      'Annulation',
      `Le client peut annuler sans frais jusqu’à ${PAYMENT_DISCLOSURE.freeCancellationHours} heures avant le début du créneau. Passé ce délai, la commande reste due selon les modalités de paiement choisies, sauf accord du producteur.`,
    ),
    article(
      'Non-retrait',
      `Si le producteur signale, dans la journée du créneau, que la commande n’a pas été retirée, un forfait de ${PAYMENT_DISCLOSURE.penaltyLabel} peut être prélevé sur l’empreinte. Ce forfait est fixe, jamais un pourcentage du panier. Il est versé à Champ’Table, jamais au producteur, afin de ne créer aucune incitation à un signalement mensonger.`,
      'Le forfait n’est pas déclenché si le client a déjà confirmé le retrait (bouton ou scan par le producteur). En cas de contradiction entre les deux signalements, aucun prélèvement automatique n’a lieu : le dossier est examiné.',
      'Après plusieurs non-retraits confirmés, Champ’Table peut imposer le paiement en ligne pour les commandes suivantes.',
    ),
    article(
      'Anti-gaspi et porte-monnaie',
      'Le paiement en ligne peut ouvrir l’accès prioritaire à certaines ventes anti-gaspi, selon les règles affichées au moment de la commande. Ces règles ne créent pas un droit à réduction automatique sur l’ensemble du catalogue.',
    ),
    article(
      'Rétractation',
      'Les denrées alimentaires susceptibles de se détériorer ou de se périmer rapidement, et les biens descellés impropres à être renvoyés pour des raisons d’hygiène, sont en principe exclus du droit de rétractation prévu pour les contrats à distance, dans les limites du code de la consommation. Le retrait sur place d’un produit frais s’inscrit dans ce cadre.',
    ),
    article(
      'Réclamations',
      'Une réclamation sur le produit (qualité, quantité, allergène) s’adresse d’abord au producteur. Champ’Table peut faciliter le contact. Une réclamation sur le paiement ou le forfait no-show s’adresse à ' +
        LEGAL_CONTACT_EMAIL +
        '.',
    ),
  ],
};

const cguProducteurs: LegalDocument = {
  slug: 'cgu-producteurs',
  title: 'Conditions générales — producteurs',
  shortTitle: 'CGU producteurs',
  lead: DRAFT_LEAD,
  articles: [
    article(
      'Objet',
      'Les présentes régissent l’accès des producteurs à la place de marché Champ’Table. Elles s’ajoutent aux CGU du compte utilisateur. En cochant l’acceptation lors de l’activation du profil, le producteur les accepte.',
    ),
    article(
      'Intermédiaire, pas revendeur',
      'Champ’Table n’achète pas les produits et n’en devient pas propriétaire. Le producteur reste le vendeur. Cette qualification détermine le traitement de la TVA : le producteur est redevable de la TVA applicable à ses ventes (y compris le régime de franchise en base agricole, lorsqu’il y est éligible). Champ’Table est redevable de la TVA au taux normal sur ses propres sommes : commission et abonnement.',
    ),
    article(
      'Activation de la boutique',
      'La vérification du SIRET (répertoire Sirene) confirme l’existence de l’établissement, pas l’identité du déclarant. La boutique reste en brouillon, invisible et incapable de recevoir une commande, jusqu’à la validation du contrôle d’identité (KYC) réalisé par le prestataire de paiement.',
      'Le producteur garantit être habilité à engager l’exploitation (titulaire, co-titulaire, associé).',
    ),
    article(
      'Prestataire de paiement',
      'Au lancement, le prestataire unique est Stripe Connect. Champ’Table n’est pas un établissement de paiement : elle n’encaisse pas les fonds du client pour les reverser elle-même. Stripe exécute la répartition entre le producteur et Champ’Table.',
      'L’IBAN et les pièces d’identité ne transitent jamais par Champ’Table.',
    ),
    article(
      'Commission, abonnement, report et SEPA',
      'Une commission de 1 % s’applique à l’ensemble du chiffre d’affaires réalisé via la plateforme (payé en ligne et payé sur place). L’abonnement mensuel suit la grille des paliers de chiffre d’affaires publiée sur le tableau de bord.',
      'Un producteur peut débloquer un palier supérieur avant d’en avoir le chiffre d’affaires sur la plateforme : l’abonnement vaut alors le tarif du palier visé, majoré de 100 % de l’écart avec le palier justifié par ce chiffre d’affaires. Dès que le chiffre d’affaires atteint ce palier, le tarif standard s’applique. Ce déblocage anticipé n’est pas un booster de visibilité.',
      'Un producteur peut demander à redescendre de palier : la descente prend effet au 1er du mois suivant, après un avertissement sur les services perdus et les produits qui devront être archivés. Rien ne change jusqu’à cette date, et la demande peut être annulée. Une montée automatique du chiffre d’affaires sur la plateforme ou un boost de palier annulent une descente programmée.',
      'Sur un paiement en ligne, la commission est prélevée par Stripe au moment de la transaction (frais d’application). Le chiffre d’affaires payé sur place et l’abonnement dû sont rattrapés sur les transactions en ligne suivantes. À défaut de rattrapage pendant trois mois, un prélèvement SEPA de dernier recours peut être présenté, conformément au mandat signé chez le prestataire.',
    ),
    article(
      'Obligations du producteur',
      'Le producteur est seul responsable de la conformité sanitaire, de l’étiquetage, des allergènes, des prix affichés et des stocks. Il honore les commandes aux créneaux annoncés et signale un non-retrait de bonne foi, uniquement lorsque la commande n’a pas été retirée.',
      'Les mises en avant payantes (boosters) portent la mention « Sponsorisé ». Leur tarif exact sera publié avant toute commercialisation ; tant qu’il n’est pas fixé, aucun booster n’est vendu.',
    ),
    article(
      'Résiliation',
      'Le producteur peut cesser d’utiliser la plateforme après honorer les commandes en cours et les sommes dues. Champ’Table peut suspendre une boutique en cas de SIRET fermé, d’échec KYC, de manquement grave, ou de taux de signalements no-show anormalement élevé.',
    ),
  ],
};

const confidentialite: LegalDocument = {
  slug: 'confidentialite',
  title: 'Politique de confidentialité',
  shortTitle: 'Confidentialité',
  lead: DRAFT_LEAD,
  articles: [
    article(
      'Responsable de traitement',
      'Le responsable de traitement est l’éditeur du service Champ’Table (voir mentions légales). Contact : ' +
        LEGAL_CONTACT_EMAIL +
        '.',
    ),
    article(
      'Données traitées',
      'Compte : nom, e-mail, mot de passe (haché par le prestataire d’authentification), téléphone et adresse s’ils sont fournis, préférences de notification.',
      'Commandes : contenu du panier, point de retrait, créneau, statut de paiement. Les données de carte sont traitées par Stripe, hors des serveurs Champ’Table.',
      'Producteur : SIRET, raison sociale, adresse d’exploitation, catégories, photographies, coordonnées professionnelles, rôle dans l’exploitation.',
      'Traces techniques : journaux d’accès, identifiant de session. La géolocalisation du navigateur n’est demandée que sur action du visiteur, pour classer les producteurs par proximité ; elle n’est pas exigée pour consulter le catalogue.',
    ),
    article(
      'Finalités et bases',
      'Exécution du contrat : création de compte, commande, paiement, notifications transactionnelles.',
      'Intérêt légitime : sécurité, prévention de la fraude, statistiques d’usage agrégées, amélioration du service.',
      'Obligation légale : facturation des commissions et abonnements, réponses aux autorités.',
      'Consentement : notifications commerciales éventuellement proposées, cookies non essentiels s’ils sont introduits.',
    ),
    article(
      'Destinataires et localisation',
      'Les données sont hébergées dans l’Union européenne (région AWS eu-west-3, Paris). Sont susceptibles d’y accéder : l’éditeur, Stripe (paiements), Amazon Web Services (hébergement), et le prestataire d’e-mails transactionnels.',
      'Un producteur voit les informations nécessaires à honorer une commande (nom, coordonnées de retrait, contenu). Il n’accède pas au catalogue des autres clients.',
    ),
    article(
      'Durées',
      'Le compte est conservé tant qu’il est actif, puis archivé le temps des obligations légales (pièces de commande et de paiement). Les journaux techniques sont conservés une durée courte, proportionnée à la sécurité et au coût.',
    ),
    article(
      'Droits',
      'Toute personne peut demander l’accès, la rectification, l’effacement, la limitation, la portabilité, ou s’opposer à un traitement fondé sur l’intérêt légitime, dans les limites du droit applicable. Ces demandes s’adressent à ' +
        LEGAL_CONTACT_EMAIL +
        '. Un recours est possible auprès de la CNIL.',
    ),
  ],
};

const charteAvis: LegalDocument = {
  slug: 'charte-avis',
  title: 'Charte des avis',
  shortTitle: 'Charte des avis',
  lead: DRAFT_LEAD,
  articles: [
    article(
      'Qui peut noter',
      'Seuls les clients ayant retiré une commande peuvent laisser un avis sur les produits et le producteur concernés. L’avis est un achat vérifié. Les comptes producteur ne notent pas leurs propres ventes.',
    ),
    article(
      'Publication de la moyenne',
      `La note moyenne n’est affichée publiquement qu’à partir de ${MIN_REVIEWS_FOR_AVERAGE} avis. En dessous, le nombre d’avis peut être indiqué, pas la moyenne : un unique commentaire ne doit pas fixer la réputation d’une ferme qui démarre.`,
    ),
    article(
      'Contenu interdit',
      'Sont refusés les avis hors sujet, injurieux, discriminatoires, publicitaires, copiés, manifestement faux, ou révélant des données personnelles d’un tiers. Un désaccord sur le goût n’est pas un motif de retrait, s’il reste courtois et lié à la commande.',
    ),
    article(
      'Modération',
      'Un premier filtre automatisé peut écarter les contenus manifestement illicites. Les cas ambigus, les signalements et les conflits (retrait confirmé par le client et contesté par le producteur, par exemple) sont examinés par une personne. Cette organisation sera précisée avec un juriste avant le lancement du module d’avis.',
    ),
    article(
      'Réponse du producteur',
      'Le producteur peut répondre publiquement à un avis portant sur sa boutique, sans harceler le client et sans révéler d’information de commande inutile au public.',
    ),
    article(
      'Conservation et signalement',
      'Un avis publié peut être conservé aussi longtemps que la fiche producteur est visible, sauf exercice d’un droit d’effacement justifié. Un contenu illicite se signale via ' +
        LEGAL_CONTACT_EMAIL +
        '.',
    ),
  ],
};

export const LEGAL_DOCUMENTS: readonly LegalDocument[] = [
  mentions,
  cgu,
  cgv,
  cguProducteurs,
  confidentialite,
  charteAvis,
];

export function legalDocument(slug: string): LegalDocument | undefined {
  return LEGAL_DOCUMENTS.find((document) => document.slug === slug);
}

export function isLegalSlug(value: string): value is LegalSlug {
  return LEGAL_DOCUMENTS.some((document) => document.slug === value);
}
